import Vue from 'vue';
import Axios from 'axios';
import qs from 'qs';
import Moment from 'moment';
import { LANGUAGES_LIST } from '@/_helpers/constants.js';

// Если подключаются другие дополнительные фильтры, то нужно добавить их в filterObj и ниже в state.filter
// В случае null даты она инорируется при fetchResponses
const filterDefaults = headerId => {
  const filterObj = {
    vacancy_id: [],
    'vacancy.language_id': [],
    response_status_id: [],
    'vacancy.user_id': [],
    created_at: { from: null, to: null },
    date_sent: { from: null, to: null },
    tag_id: []
  };

  if (headerId === 'created_at') {
    return { created_at: { from: null, to: null } };
  }

  if (headerId === 'date_sent') {
    return { date_sent: { from: null, to: null } };
  }

  if (headerId === 'resetAll') {
    return filterObj;
  }

  return { [headerId]: [] };
};

// consts for resetting to default preset
const unselectedDefault = [];
const selectedDefault = [
  'id',
  'candidate',
  'email',
  'phone',
  'vacancy',
  'vacancyLanguage',
  'status',
  'interview_refusal',
  'rating',
  'created_at',
  'date_sent',
  'social_source',
  'creator',
  'tag_id',
  'comments'
];

const TODAY = Moment().format('YYYY-MM-DD');

const getDefaultState = () => ({
  responses: [],
  selectedResponses: [],
  responsesVacancies: [], // vacancies for filter in top of responses
  responsesVacanciesUsers: [], // vacancies creators unique by id
  isLoading: false,
  isLoadingStatuses: false,
  isLoadingMore: false,
  isLoadingExport: false,
  sort: {
    order: 'desc', // asc || desc
    by: ''
  },
  // Filter changes locally in component and send in vuex after applying filter button
  responsesFilterTable: {
    vacancy_id: [],
    'vacancy.language_id': [],
    response_status_id: [],
    'vacancy.user_id': [],
    created_at: { from: TODAY, to: TODAY },
    date_sent: { from: TODAY, to: TODAY },
    tag_id: []
  },
  responsesFilter: {
    vacancy_id: [],
    'vacancy.language_id': [],
    response_status_id: [],
    'vacancy.user_id': [],
    created_at: { from: null, to: null },
    date_sent: { from: null, to: null },
    tag_id: []
  },
  responsesPagination: {
    _page: 1, // default 1
    _page_count: 0,
    _total_items: 0,
    limit: 20 // default 20
  },

  // statuses
  responseStatuses: [],

  // email templates
  responseEmailTemplatesDefault: [],

  // table presets
  isLoadingPresets: false,
  tablePresets: [],
  isShowModalNewPreset: false,

  // table headers
  responsesHeadersUnselectedDefault: [],
  responsesHeadersSelectedDefault: [],
  responsesHeadersUnselected: [],
  responsesHeadersSelected: []
});

const state = getDefaultState();

const getters = {
  getResponsesQuery: state => {
    return qs.stringify({ sort: state.sort, filter: state.responsesFilter }, { encode: false });
  },

  getComputedHeaders: (state, getters, rootState) => {
    return [
      { id: 'id', name: '', width: '50px' },
      { id: 'candidate', name: window.$tr('common.list.candidate'), width: '215px', isSort: true },
      { id: 'email', name: window.$tr('common.email'), width: '' },
      { id: 'phone', name: window.$tr('common.phone-number'), width: '' },
      {
        id: 'vacancy',
        filterId: 'vacancy_id',
        name: window.$tr('common.list.vacancy'),
        width: '215px',
        isFilter: true,
        filterType: 'list',
        filterList: getters.getResponsesVacanciesSelect
      },
      {
        id: 'vacancyLanguage',
        filterId: 'vacancy.language_id',
        name: window.$tr('vacancy.create.language'),
        width: '215px',
        isFilter: true,
        filterType: 'list',
        filterList: LANGUAGES_LIST.map(x => ({ value: x.id, text: x.name }))
      },
      {
        id: 'status',
        filterId: 'response_status_id',
        name: window.$tr('common.list.status'),
        width: '',
        isSort: false,
        isFilter: true,
        filterType: 'list',
        filterList: state.responseStatuses
      },
      { id: 'interview_refusal', name: window.$tr('common.list.refusalReason'), width: '' },
      { id: 'rating', name: window.$tr('common.list.rating'), width: '150px' },
      {
        id: 'created_at',
        name: window.$tr('common.list.created'),
        isSort: true,
        isFilter: true,
        filterType: 'date',
        width: '',
        dates: {
          startDate: state.responsesFilterTable.created_at.from,
          endDate: state.responsesFilterTable.created_at.to
        }
      },
      {
        id: 'date_sent',
        name: window.$tr('admin_console.response.tests.endDate'),
        width: '',
        isFilter: true,
        filterType: 'date',
        dates: {
          startDate: state.responsesFilterTable.date_sent.from,
          endDate: state.responsesFilterTable.date_sent.to
        }
      },
      { id: 'social_source', name: window.$tr('common.list.source'), width: '' },
      {
        id: 'creator',
        filterId: 'vacancy.user_id',
        name: window.$tr('common.list.vacancyCreator'),
        width: '',
        isFilter: true,
        filterType: 'list',
        filterList: state.responsesVacanciesUsers
      },
      {
        id: 'tag_id',
        name: window.$tr('common.list.tags'),
        width: '',
        isFilter: true,
        filterType: 'list',
        filterList: rootState.vcvTags.vcv_tags_list.map(x => ({ value: x.id, text: x.name }))
      },
      { id: 'comments', name: window.$tr('common.list.comments'), width: '' }
    ];
  },

  getCommentsByResponseId: state => responseId => {
    const response = state.responses.find(x => x.id === responseId);

    if (response !== undefined) {
      const commentsCopy = [...response.comments];

      return commentsCopy.reverse();
    } else {
      return [];
    }
  },

  getResponseNameById: state => responseId => {
    const response = state.responses.find(x => x.id === responseId);

    if (response !== undefined) {
      return response.candidate?.name;
    }

    return '';
  },

  getResponsesVacanciesSelect: state => {
    return state.responsesVacancies.map(x => ({ value: x.id, text: x.title, active: x.active }));
  },

  getResponsesTablePresets: state => {
    return [
      {
        id: 'default',
        name: window.$tr('common.table.default'),
        is_active: state.tablePresets.some(x => x.is_active) ? false : true, // делать активным если остальные пресеты неактивны
        isBase: true
      },
      ...state.tablePresets
    ];
  },

  getResponsesCurrentPreset: state => {
    const firstActivePreset = state.tablePresets.find(x => x.is_active);

    if (firstActivePreset !== undefined) {
      return firstActivePreset.id;
    }

    return 'default';
  },

  isSelectAll: state => {
    if (state.responses.length > 0) {
      const responsesList = state.responses.map(x => x.id);

      return (
        responsesList.length == state.selectedResponses.length &&
        responsesList.every(element => {
          // return element === state.selectedResponses[index];
          if (state.selectedResponses.indexOf(element) > -1) {
            return (element = state.selectedResponses[state.selectedResponses.indexOf(element)]);
          }
        })
      );
    }

    return false;
  },

  getSelectedCandidates: state => {
    return state.responses
      .filter(x => state.selectedResponses.includes(x.id))
      .map(x => {
        return {
          id: x.id,
          name: x.candidate?.name,
          email: x.email,
          status: x.status,
          vacancy: x.vacancy,
          vacancyLanguage: x.vacancyLanguage
        };
      });
  },

  getResponsesGroupedByVacancy: (state, getters) => {
    let responsesGroupedByVacancy = [...state.responsesVacancies];

    getters.getSelectedCandidates.forEach(response => {
      const vacancyIndex = responsesGroupedByVacancy.findIndex(x => x.id === response.vacancy.id);

      if (vacancyIndex >= 0) {
        if (responsesGroupedByVacancy[vacancyIndex].responses !== undefined) {
          const isResponseExist = responsesGroupedByVacancy[vacancyIndex].responses.find(x => x.id === response.id);

          if (isResponseExist === undefined) {
            responsesGroupedByVacancy[vacancyIndex].responses.push(response);
          }
        } else {
          // https://ru.vuejs.org/v2/guide/reactivity.html
          Vue.set(responsesGroupedByVacancy, vacancyIndex, {
            ...responsesGroupedByVacancy[vacancyIndex],
            responses: [response]
          });
        }
      } else {
        // Такое бывает если вакансия на которую откликался кандидат удалена. Тогда пишем, что "отклик с удаленной вакансии"
        Vue.set(responsesGroupedByVacancy, responsesGroupedByVacancy.length, {
          id: null,
          title: window.$tr('responses.from-deleted-vacancies'),
          responses: [response]
        });
      }
    });

    responsesGroupedByVacancy = responsesGroupedByVacancy.filter(vacancy => vacancy.responses !== undefined);

    return responsesGroupedByVacancy;
  }
};

const mutations = {
  SET_RESPONSES: (state, responses) => {
    state.responses = responses;
  },

  ADD_RESPONSES: (state, responses) => {
    state.responses = [...state.responses, ...responses];
  },

  REMOVE_RESPONSE: (state, responseId) => {
    const responseIndex = state.responses.findIndex(x => x.id === responseId);

    if (responseIndex >= 0) {
      state.responses.splice(responseIndex, 1);
    }
  },

  SET_RESPONSES_IS_LOADING: (state, value) => {
    state.isLoading = value;
  },

  SET_RESPONSES_STATUSES_IS_LOADING: (state, value) => {
    state.isLoadingStatuses = value;
  },

  SET_RESPONSES_IS_LOADING_MORE: (state, value) => {
    state.isLoadingMore = value;
  },

  SET_RESPONSES_EXPORT_IS_LOADING: (state, value) => {
    state.isLoadingExport = value;
  },

  SET_RESPONSES_IS_LOADING_PRESETS: (state, value) => {
    state.isLoadingPresets = value;
  },

  ADD_SELECTED_RESPONSE: (state, response) => {
    state.selectedResponses.push(response);
  },

  REMOVE_SELECTED_RESPONSE: (state, responseId) => {
    const index = state.selectedResponses.indexOf(responseId);

    state.selectedResponses.splice(index, 1);
  },

  RESET_SELECTED_RESPONSES: state => {
    state.selectedResponses = [];
  },

  SET_RESPONSE_DISABLED: (state, { responseId, value }) => {
    const responseIndex = state.responses.findIndex(x => x.id == responseId);

    if (responseIndex >= 0) {
      state.responses[responseIndex].isDisabled = value;
    }
  },

  TOGGLE_ALL_RESPONSES: (state, checkbox) => {
    state.responses.forEach(element => {
      if (checkbox) {
        if (state.selectedResponses.indexOf(element.id) < 0) {
          state.selectedResponses.push(element.id);
        }
      } else {
        const index = state.selectedResponses.indexOf(element.id);
        state.selectedResponses.splice(index, 1);
      }
    });
  },

  SET_RESPONSES_SORT: (state, sorting) => {
    state.sort.order = sorting.order;
    state.sort.by = sorting.by;
  },

  SET_RESPONSES_SORT_DEFAULT: state => {
    state.sort.order = 'desc';
    state.sort.by = '';
  },

  // RESPONSES FILTERS
  SET_RESPONSES_FILTER: (state, data) => {
    state.responsesFilter[data.property] = data.value;
  },

  SET_RESPONSES_FILTER_DEFAULT: (state, headerId) => {
    Object.assign(state.responsesFilter, filterDefaults(headerId));
    Object.assign(state.responsesFilterTable, filterDefaults(headerId));
  },

  SET_RESPONSES_ALL_FILTER_DEFAULT: state => {
    Object.assign(state.responsesFilter, filterDefaults('resetAll'));
  },

  SET_RESPONSES_FILTER_TABLE: (state, data) => {
    state.responsesFilterTable[data.property] = data.value;
  },

  SET_RESPONSES_FILTER_LIST: (state, data) => {
    const index = state.responsesFilter[data.property].indexOf(data.value);

    if (index < 0) {
      state.responsesFilter[data.property].push(data.value);
    } else {
      state.responsesFilter[data.property].splice(index, 1);
    }
  },

  SET_RESPONSES_FILTER_TABLE_LIST: (state, data) => {
    const index = state.responsesFilterTable[data.property].indexOf(data.value);

    if (index < 0) {
      state.responsesFilterTable[data.property].push(data.value);
    } else {
      state.responsesFilterTable[data.property].splice(index, 1);
    }
  },

  CANCEL_RESPONSES_FILTER: (state, headerId) => {
    // Даты сделаны объектом в фильтре, поэтому и сбрасываются как объект. Деструктуризация сделана, чтобы переменные не наследовали друг друга из responsesFilterTable в responsesFilter
    if (headerId === 'created_at' || headerId === 'date_sent') {
      // Если предыдущее состояние было без дат ({ from: null, to: null }), то в responsesFilterTable вернуть значение сегодняшнего дня, чтобы они отображались в поле.
      if (state.responsesFilter[headerId].from === null && state.responsesFilter[headerId].to === null) {
        state.responsesFilterTable[headerId].from = TODAY;
        state.responsesFilterTable[headerId].to = TODAY;
      } else {
        state.responsesFilterTable[headerId] = { ...state.responsesFilter[headerId] };
      }
    } else {
      state.responsesFilterTable[headerId] = [...state.responsesFilter[headerId]];
    }
  },

  COPY_RESPONSES_FILTER_TABLE_TO_FILTER: (state, { withDateReset, headerId }) => {
    state.responsesFilter = { ...state.responsesFilterTable };

    if (withDateReset !== undefined && withDateReset === true) {
      state.responsesFilterTable[headerId] = { from: TODAY, to: TODAY };
      // state.responsesFilterTable[headerId].to = TODAY;
      state.responsesFilter[headerId] = { from: null, to: null };
      // state.responsesFilter[headerId].from = null;
    }
  },

  SET_RESPONSES_FILTERS: (state, filters) => {
    state.responsesFilter = filters;
  },

  SET_RESPONSES_FILTERS_TABLE: (state, filters) => {
    state.responsesFilterTable = filters;
  },

  SET_RESPONSES_PAGINATION: (state, { property, value }) => {
    state.responsesPagination[property] = value;
  },

  RESET_RESPONSES_PAGINATION: state => {
    state.responsesPagination._page = 1;
    state.responsesPagination.limit = 20;
  },

  ADD_COMMENT: (state, { responseId, comment }) => {
    const responseIndex = state.responses.findIndex(x => x.id === responseId);

    if (responseIndex >= 0) {
      state.responses[responseIndex].comments.unshift(comment);
    }
  },

  // HEADERS
  SET_RESPONSES_HEADERS: (state, { collection, newArray }) => {
    // collection === responsesHeadersUnselected || responsesHeadersSelected || responsesHeadersUnselectedDefault || responsesHeadersSelectedDefault
    state[collection] = newArray;
  },

  RESET_RESPONSES_HEADERS_TO_DEFAULT: state => {
    state.responsesHeadersSelected = state.responsesHeadersSelectedDefault;
    state.responsesHeadersUnselected = state.responsesHeadersUnselectedDefault;
  },

  SET_RESPONSES_HEADERS_TO_DEFAULT_PRESET: state => {
    state.responsesHeadersSelected = selectedDefault;
    state.responsesHeadersUnselected = unselectedDefault;
    state.responsesHeadersSelectedDefault = selectedDefault;
    state.responsesHeadersUnselectedDefault = unselectedDefault;
  },

  // VACANCIES
  // Нужны для фильтров
  SET_RESPONSES_VACANCIES: (state, data) => {
    state.responsesVacancies = data;
  },

  SET_RESPONSES_VACANCIES_USERS: (state, data) => {
    state.responsesVacanciesUsers = data;
  },

  // STATUSES
  SET_RESPONSE_STATUSES: (state, statuses) => {
    state.responseStatuses = statuses;
  },

  SET_RESPONSE_STATUS: (state, { responseId, newStatusId }) => {
    const responseIndex = state.responses.findIndex(x => x.id === responseId);
    const statusObject = state.responseStatuses.find(x => x.value === newStatusId);

    if (responseIndex >= 0 && statusObject !== undefined) {
      Vue.set(state.responses[responseIndex], 'status', { id: statusObject.value, title: statusObject.text });
    }
  },

  // EMAIL TEMPLATES
  SET_RESPONSE_EMAIL_TEMPLATES: (state, templates) => {
    state.responseEmailTemplatesDefault = templates;
  },

  CLEAR_RESPONSES_STATE: state => {
    Object.assign(state, getDefaultState());
  },

  // PRESETS
  SET_TABLE_PRESETS: (state, presets) => {
    // Так как конфиг пресета хранится в JSON.stringify, то для удобства работы в дальнейшем делаем каждому пресету JSON.parse
    for (let index = 0; index < presets.length; index++) {
      presets[index].config = JSON.parse(presets[index].config);
      state.tablePresets.push(presets[index]);
    }
  },

  ADD_TABLE_PRESET: (state, preset) => {
    state.tablePresets.push(preset);
  },

  SET_TABLE_PRESET_PROPERTY: (state, { presetId, property, value }) => {
    const presetIndex = state.tablePresets.findIndex(x => x.id === presetId);

    if (presetIndex >= 0) {
      state.tablePresets[presetIndex][property] = value;
    }
  },

  SET_IS_SHOW_MODAL_NEW_PRESET: (state, bool) => {
    state.isShowModalNewPreset = bool;
  },

  REMOVE_TABLE_PRESET: (state, presetId) => {
    const presetIndex = state.tablePresets.findIndex(x => x.id === presetId);

    if (presetIndex >= 0) {
      state.tablePresets.splice(presetIndex, 1);
    }
  }
};

const actions = {
  async fetchResponses({ state, commit }, fetchMore = false) {
    if (fetchMore) {
      commit('SET_RESPONSES_IS_LOADING_MORE', true);
    } else {
      commit('SET_RESPONSES_IS_LOADING', true);
    }

    // Проверяем если установлена сегодняшняя дата в полях from и to, то фильтр из запроса исключаем (аналог сброшенного фильтра)
    const filter = { ...state.responsesFilter };

    if (
      (filter.date_sent.from === null && filter.date_sent.to === null) ||
      (filter.date_sent.from === filter.date_sent.to && filter.date_sent.from === TODAY)
    ) {
      delete filter.date_sent;
    }
    if (
      (filter.created_at.from === null && filter.created_at.to === null) ||
      (filter.created_at.from === filter.created_at.to && filter.created_at.from === TODAY)
    ) {
      delete filter.created_at;
    }

    if (fetchMore) {
      commit('SET_RESPONSES_PAGINATION', { property: '_page', value: state.responsesPagination._page + 1 });
    }

    const params = {
      with: [
        'vacancy',
        'vacancy.language',
        'vacancy.userName',
        'candidate',
        'candidate.name',
        'candidate.phone',
        'source',
        'status',
        // 'interview',
        'interview.interviewRefusal',
        'tags',
        'comments',
        'comments.user',
        'comments.userName'
      ],
      sort: state.sort,
      filter,
      limit: state.responsesPagination.limit,
      page: state.responsesPagination._page
    };

    await Axios.get('/v3/responses', { params, paramsSerializer: params => qs.stringify(params) })
      .then(response => {
        const responses = response.data._embedded.responses.map(x => {
          let isInterviewRefusal = '';

          if (
            x._embedded !== undefined &&
            x._embedded.interview !== undefined &&
            x._embedded.interview._embedded !== undefined &&
            x._embedded.interview._embedded.interviewRefusal !== undefined &&
            x._embedded.interview._embedded.interviewRefusal !== null &&
            x._embedded.interview._embedded.interviewRefusal.reason !== undefined
          ) {
            isInterviewRefusal = x._embedded.interview._embedded.interviewRefusal.reason;
          }

          return {
            id: x.id,
            candidate: {
              preview: x.preview,
              name: `${x.candidate.name?.first_name} ${x.candidate.name?.last_name}`,
              gender: x.candidate.gender,
              age: x.candidate.birthdate !== null ? this.$moment().diff(x.candidate.birthdate, 'years') : null,
              resume_url: x.resume_url || null,
              second_resume_url: x.second_resume_url || null
            },
            email: x.candidate.email || '',
            phone: x.candidate.phone !== null ? x.candidate.phone.number : '',
            vacancy: {
              id: x._embedded.vacancy.id,
              name: x._embedded.vacancy.title,
              active: x._embedded.vacancy.active
            },
            vacancyLanguage: x._embedded.vacancy.language?.code,
            status: {
              id: x._embedded.status.id,
              title: x._embedded.status.title
            },
            interview_refusal: isInterviewRefusal,
            rating: x.rating,
            created_at: x.created_at !== null ? Moment(x.created_at).format('YYYY-MM-DD HH:mm') : null,
            date_sent: x.date_sent !== null ? Moment(x.date_sent).format('YYYY-MM-DD HH:mm') : null,
            social_source: x.social_source || '',
            creator:
              x._embedded.vacancy.userName !== undefined
                ? `${x._embedded.vacancy?.userName?.first_name || ''} ${x._embedded.vacancy?.userName?.last_name || ''}`
                : '',
            tag_id: x._embedded.tags.reverse() || [],
            comments: x._embedded.comments || [],
            isDisabled: x.is_hidden_by_candidate || !!isInterviewRefusal || false,
            isHiddenByCandidate: x.is_hidden_by_candidate
          };
        });

        if (fetchMore) {
          commit('ADD_RESPONSES', responses);
        } else {
          commit('SET_RESPONSES', responses);
        }

        commit('SET_RESPONSES_PAGINATION', { property: '_page_count', value: response.data._page_count || 0 });
        commit('SET_RESPONSES_PAGINATION', { property: '_total_items', value: response.data._total_items || 0 });
      })
      .finally(() => {
        commit('SET_RESPONSES_IS_LOADING', false);
        commit('SET_RESPONSES_IS_LOADING_MORE', false);
      });
  },

  async fetchResponseStatuses({ commit }) {
    commit('SET_RESPONSES_STATUSES_IS_LOADING', true);

    const params = { limit: 9999, fields: ['responses_count', 'is_base'], with: ['parent'], filter: { hidden: false } };

    await Axios.get('/v3/response-statuses', { params, paramsSerializer: params => qs.stringify(params) })
      .then(response => {
        commit(
          'SET_RESPONSE_STATUSES',
          response.data._embedded.statuses
            .filter(x => x.active)
            .map(x => ({
              onTop: x.on_top,
              sortOrder: x.sort_order,
              value: x.id,
              parentId: x.parent_id, // Нужен, блять, только для переводов (не пытайся это понять)
              text:
                x.parent_id === null || (x.parent !== null && x.title !== x._embedded?.parent?.title)
                  ? x.title
                  : window.$tr(`table.response-statuses.${x.parent_id === null ? x.id : x.parent_id}`),
              responsesCount: x.response_count
            }))
        );
      })
      .finally(() => {
        commit('SET_RESPONSES_STATUSES_IS_LOADING', false);
      });
  },

  async responsesXlsExport({ commit, dispatch }, params) {
    commit('SET_RESPONSES_EXPORT_IS_LOADING', true);

    await Axios.get('/1.0/responses/report/xls', {
      params,
      paramsSerializer: params => qs.stringify(params)
    })
      .catch(err => {
        dispatch('global/setError', err, { root: true });
      })
      .finally(() => {
        commit('SET_RESPONSES_EXPORT_IS_LOADING', false);
      });
  },

  async fetchVacancies({ commit }) {
    await Axios.get(`/v3/vacancies`, {
      params: { limit: 9999, active: true, with: ['user.userName'] },
      paramsSerializer: params => qs.stringify(params)
    })
      .catch(() => {
        this.$noty.error('common.error');
      })
      .then(response => {
        // make array of unique users
        const nonUniqueUsers = response.data._embedded.vacancies
          .filter(x => x._embedded !== undefined)
          .map(x => {
            if (
              x._embedded !== undefined &&
              x._embedded.user !== undefined &&
              x._embedded.user.userName !== undefined
            ) {
              let userName = '';
              const firstName = x._embedded.user.userName.first_name || '';
              const lastName = x._embedded.user.userName.last_name || '';

              if (firstName !== '') {
                userName += ` ${firstName}`;
              }
              if (lastName !== '') {
                userName += ` ${lastName}`;
              }

              return { value: x._embedded.user.id, text: userName.slice(1) };
            } else {
              console.log(x);
            }
          });

        // console.log(nonUniqueUsers);

        const uniqueUsers = nonUniqueUsers.reduce(
          (acc, user) => {
            if (acc.map[user.value])
              // если данный пользователь уже был
              return acc; // ничего не делаем, возвращаем уже собранное

            acc.map[user.value] = true; // помечаем пользователя, как обработанного
            acc.users.push(user); // добавляем объект в массив пользователей

            return acc; // возвращаем собранное
          },
          {
            map: {}, // здесь будут отмечаться обработанные пользователи
            users: [] // здесь конечный массив уникальных пользователей
          }
        ).users; // получаем конечный массив

        // console.log(uniqueUsers);

        // Удаляем из каждого объекта _embedded, _links через деструктуризацию ES8
        const vacancies = response.data._embedded.vacancies.map(
          ({ _embedded, _links, ...keepedAttributes }) => keepedAttributes
        );
        commit('SET_RESPONSES_VACANCIES', Object.freeze(vacancies));
        commit('SET_RESPONSES_VACANCIES_USERS', uniqueUsers);
      });
  },

  async fetchResponseStatusEmailTemplates({ commit }) {
    const params = { limit: 9999, with: ['language'] };

    await Axios.get('/v3/response-status-email-template-defaults', {
      params,
      paramsSerializer: params => qs.stringify(params)
    }).then(response => {
      // Собираем объект поменьше
      const templatesDefault = response.data._embedded.templates.map(x => {
        return {
          value: x.id,
          text: x.title,
          templateHeader: x.template_header,
          templateText: x.template_text,
          language: x.language.code
        };
      });

      // И замораживаем его, чтобы память не хавал
      commit('SET_RESPONSE_EMAIL_TEMPLATES', Object.freeze(templatesDefault));
    });
  },

  // PRESETS
  async fetchResponsesTablePresets({ commit }, userId) {
    commit('SET_RESPONSES_IS_LOADING_PRESETS', true);

    await Axios.get(`/v3/table/presets?filter[user_id]=${userId}&filter[type]=responses`)
      .then(response => {
        const presets = response.data._embedded.table_presets;

        commit('SET_TABLE_PRESETS', presets);

        // Если пресетов у пользователя нет, то будет выбран пресет по умолчанию и нужно заполнить reponsesHeadersSelected из responsesHeadersSelectedDefault
        if (presets.length === 0) {
          commit('SET_RESPONSES_HEADERS_TO_DEFAULT_PRESET');
          commit('SET_RESPONSES_HEADERS', {
            collection: 'responsesHeadersSelected',
            newArray: state.responsesHeadersSelectedDefault
          });
          commit('SET_RESPONSES_HEADERS', {
            collection: 'responsesHeadersUnselected',
            newArray: state.responsesHeadersUnselectedDefault
          });
        } else {
          // В остальных случаях находим активный пресет и заполняем из него дефолтные хэдеры и обычные
          const activePreset = presets.find(x => x.is_active);

          if (activePreset && activePreset.config !== null) {
            // Если активный пресет есть и его конфиг не равен null, то берем дефолтные значения из него

            commit('SET_RESPONSES_HEADERS', {
              collection: 'responsesHeadersSelected',
              newArray: activePreset.config.responsesHeadersSelected || selectedDefault
            });
            commit('SET_RESPONSES_HEADERS', {
              collection: 'responsesHeadersUnselected',
              newArray: activePreset.config.responsesHeadersUnselected || unselectedDefault
            });
            commit('SET_RESPONSES_HEADERS', {
              collection: 'responsesHeadersSelectedDefault',
              newArray: activePreset.config.responsesHeadersSelected || selectedDefault
            });
            commit('SET_RESPONSES_HEADERS', {
              collection: 'responsesHeadersUnselectedDefault',
              newArray: activePreset.config.responsesHeadersUnselected || unselectedDefault
            });

            if (activePreset.is_save_filters && activePreset.config.filter !== undefined) {
              commit('SET_RESPONSES_FILTERS', activePreset.config.filter);
              commit('SET_RESPONSES_FILTERS_TABLE', activePreset.config.filter);
            }
          } else {
            // Если активных пресетов нет, то устанавливаем на дефолтные значения (из захардкоженных сверху)
            commit('SET_RESPONSES_HEADERS_TO_DEFAULT_PRESET');
            commit('SET_RESPONSES_HEADERS', {
              collection: 'responsesHeadersSelected',
              newArray: state.responsesHeadersSelectedDefault
            });
            commit('SET_RESPONSES_HEADERS', {
              collection: 'responsesHeadersUnselected',
              newArray: state.responsesHeadersUnselectedDefault
            });
          }
        }
      })
      .finally(() => {
        commit('SET_RESPONSES_IS_LOADING_PRESETS', false);
      });
  },

  async addResponsesTablePreset({ commit, rootState }, { presetName, presetSaveFilters }) {
    commit('SET_RESPONSES_IS_LOADING_PRESETS', true);

    let newConfig = {
      responsesHeadersSelected: state.responsesHeadersSelected,
      responsesHeadersUnselected: state.responsesHeadersUnselected
    };

    if (presetSaveFilters) {
      newConfig.filter = state.responsesFilter;
    }

    const data = {
      user_id: rootState.user.data.id,
      type: 'responses',
      is_active: true,
      is_save_filters: presetSaveFilters,
      name: presetName,
      config: JSON.stringify(newConfig)
    };

    return await Axios.post(`/v3/table/presets`, data)
      .then(response => {
        if (response.status !== undefined && response.status === 200) {
          commit('ADD_TABLE_PRESET', response.data);
          commit('SET_IS_SHOW_MODAL_NEW_PRESET', false);
        }

        return response.data;
      })
      .finally(() => {
        commit('SET_RESPONSES_IS_LOADING_PRESETS', false);
      });
  },

  async saveTablePreset({ commit }, presetId) {
    commit('SET_RESPONSES_IS_LOADING_PRESETS', true);
    let currentPreset = state.tablePresets.find(x => x.id === presetId);

    let newConfig = {
      responsesHeadersSelected: state.responsesHeadersSelected,
      responsesHeadersUnselected: state.responsesHeadersUnselected
    };

    if (currentPreset.is_save_filters) {
      newConfig.filter = state.responsesFilter;
    }

    newConfig = JSON.stringify(newConfig);

    await Axios.patch(`/v3/table/presets/${presetId}`, { is_active: true, config: newConfig }).finally(() => {
      commit('SET_RESPONSES_IS_LOADING_PRESETS', false);
    });
  },

  async setTablePreset({ commit, state }, presetId) {
    commit('SET_RESPONSES_IS_LOADING_PRESETS', true);
    let currentActivePreset = state.tablePresets.find(x => x.is_active);

    if (presetId === 'default') {
      if (currentActivePreset !== undefined) {
        await Axios.patch(`/v3/table/presets/${currentActivePreset.id}`, {
          is_active: false,
          config: currentActivePreset.config
        })
          .then(() => {
            commit('SET_TABLE_PRESET_PROPERTY', {
              presetId: currentActivePreset.id,
              property: 'is_active',
              value: false
            });
            commit('SET_RESPONSES_HEADERS_TO_DEFAULT_PRESET');
          })
          .finally(() => {
            commit('SET_RESPONSES_IS_LOADING_PRESETS', false);
          });
      }
    } else {
      let newActivePreset = state.tablePresets.find(x => x.id === presetId);

      if (newActivePreset !== undefined) {
        commit('SET_RESPONSES_HEADERS', {
          collection: 'responsesHeadersSelected',
          newArray:
            newActivePreset.config !== null && newActivePreset.config.responsesHeadersSelected !== undefined
              ? newActivePreset.config.responsesHeadersSelected
              : selectedDefault
        });

        commit('SET_RESPONSES_HEADERS', {
          collection: 'responsesHeadersSelectedDefault',
          newArray:
            newActivePreset.config !== null && newActivePreset.config.responsesHeadersSelected !== undefined
              ? newActivePreset.config.responsesHeadersSelected
              : selectedDefault
        });

        commit('SET_RESPONSES_HEADERS', {
          collection: 'responsesHeadersUnselected',
          newArray:
            newActivePreset.config !== null && newActivePreset.config.responsesHeadersUnselected !== undefined
              ? newActivePreset.config.responsesHeadersUnselected
              : unselectedDefault
        });

        commit('SET_RESPONSES_HEADERS', {
          collection: 'responsesHeadersUnselectedDefault',
          newArray:
            newActivePreset.config !== null && newActivePreset.config.responsesHeadersUnselected !== undefined
              ? newActivePreset.config.responsesHeadersUnselected
              : unselectedDefault
        });

        // Записываем флаг is_active + конфиг
        await Axios.patch(`/v3/table/presets/${presetId}`, {
          is_active: true,
          config: JSON.stringify(newActivePreset.config)
        }).then(() => {
          // Если пресет с которого переходишь не default (currentActivePreset !== undefined), то ставим ему is_active: false
          if (currentActivePreset !== undefined) {
            commit('SET_TABLE_PRESET_PROPERTY', {
              presetId: currentActivePreset.id,
              property: 'is_active',
              value: false
            });
          }

          commit('SET_TABLE_PRESET_PROPERTY', { presetId, property: 'is_active', value: true });
        });

        commit('SET_RESPONSES_IS_LOADING_PRESETS', false);
      }
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
